import { APPLY_UPDATE_MESSAGE } from "../service-worker/updateMessage";

/**
 * Registering the service worker, and noticing when a newer one is standing behind it.
 *
 * **Not in development.** `app/service-worker/sw.ts` precaches the build's own output and claims
 * every client, and Vite's dev server has no build output: it has a module graph that changes on
 * every save. A worker installed there would either cache nothing useful or serve a module the
 * editor has already replaced, and the second failure looks like a bug in the code being edited.
 *
 * The consequence is that the worker only exists where it is real, which is also the only place it
 * can be honestly tested. `tests/e2e/offline-shell.spec.ts` runs against a production build served
 * by `vite preview` for exactly that reason, rather than against the dev server the rest of the
 * browser suite uses.
 *
 * Nothing here waits for the registration or reports it. A portal that could not install a service
 * worker still works: the worker adds an offline page and push, and neither is load-bearing for
 * reading a request or sending a message.
 *
 * **What is new is the waiting worker.** The worker no longer calls `skipWaiting`, so a deploy that
 * lands while somebody has the portal open installs and then stops. That is the correct behaviour
 * and it is useless on its own, because a version that waits forever is the same as no new version
 * at all. The store below is how the page finds out, and `app/pwa/UpdatePrompt.tsx` is what says so
 * on screen.
 */

export { APPLY_UPDATE_MESSAGE };

let waitingWorker: ServiceWorker | null = null;
let controllerChangeWired = false;
let applying = false;

const listeners = new Set<() => void>();

function publish(next: ServiceWorker | null): void {
  if (next === waitingWorker) {
    return;
  }

  waitingWorker = next;

  for (const listener of listeners) {
    listener();
  }
}

/**
 * Subscribe to the waiting worker, in the shape `useSyncExternalStore` wants.
 *
 * A plain module-level store rather than context, because the answer is a property of the browser
 * and not of any part of the tree. Two mounted prompts would be a mistake, but if one ever happens
 * they will agree.
 */
export function subscribeToWaitingUpdate(listener: () => void): () => void {
  listeners.add(listener);

  return () => {
    listeners.delete(listener);
  };
}

export function getWaitingUpdate(): ServiceWorker | null {
  return waitingWorker;
}

/**
 * Watch one registration for a worker that has installed and is waiting its turn.
 *
 * Two paths reach the same conclusion, and both are needed. A worker that finished installing while
 * the tab was closed is already sitting in `registration.waiting` when the page loads, and a worker
 * that installs while the page is open arrives through `updatefound`. The `controller` check on the
 * second path is what separates an update from a first install: with no controller there is no
 * previous bundle running, nothing was replaced, and there is nothing to tell anybody about.
 *
 * Exported rather than private because the whole flow has to be testable without a browser.
 * `tests/pwa-update-prompt.test.tsx` drives it with a fake registration.
 */
export function trackRegistrationForUpdates(registration: ServiceWorkerRegistration): void {
  publish(registration.waiting ?? null);

  registration.addEventListener("updatefound", () => {
    const installing = registration.installing;

    if (!installing) {
      return;
    }

    installing.addEventListener("statechange", () => {
      if (installing.state === "installed" && hasController()) {
        publish(installing);
      }
    });
  });

  wireControllerChange();
}

function hasController(): boolean {
  return typeof navigator !== "undefined" && "serviceWorker" in navigator
    ? Boolean(navigator.serviceWorker.controller)
    : false;
}

/**
 * Reload once, and only when the reload was asked for.
 *
 * Control changes hands for reasons the person did not choose, and reloading a page underneath
 * somebody who is halfway through a message is its own defect. The flag means this reload only ever
 * follows the button in the update prompt.
 */
function wireControllerChange(): void {
  if (controllerChangeWired || typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
    return;
  }

  controllerChangeWired = true;

  navigator.serviceWorker.addEventListener("controllerchange", () => {
    if (applying) {
      applying = false;
      window.location.reload();
    }
  });
}

/** Release the waiting worker. The reload follows from `controllerchange` above. */
export function applyWaitingUpdate(): void {
  const worker = waitingWorker;

  if (!worker) {
    return;
  }

  applying = true;
  worker.postMessage({ type: APPLY_UPDATE_MESSAGE });
}

export function registerServiceWorker(): void {
  if (import.meta.env.DEV) {
    return;
  }

  if (!("serviceWorker" in navigator)) {
    return;
  }

  // After `load`, so registration never competes with the first paint for bandwidth on the slow
  // connection where the offline page matters most.
  window.addEventListener("load", () => {
    void navigator.serviceWorker
      .register("/sw.js", { scope: "/" })
      .then((registration) => {
        trackRegistrationForUpdates(registration);

        // A portal tab can stay open for days. The browser checks for a new worker on navigation
        // and on its own schedule, neither of which a single-page application does much of, so the
        // tab coming back to the foreground is the honest moment to ask.
        document.addEventListener("visibilitychange", () => {
          if (document.visibilityState === "visible") {
            void registration.update().catch(() => {
              // Offline, or the check failed. There is nothing to say and nothing to do.
            });
          }
        });
      })
      .catch(() => {
        // Swallowed on purpose, and this is the one place in the portal where that is right. A
        // failed registration costs an offline page and push; it costs nothing a client is doing
        // right now, and there is no action for them to take. The browser's own console already
        // carries the reason for anybody debugging it.
      });
  });
}
